import { useState } from 'react';
import { CircleAlert, CircleCheck } from 'lucide-react';
import type { ForgeUsageSheet, UsageSheetPromiseMode, UsageSheetSourceKind } from '@shared/types/forge-usage-sheet';
import type { DesignBriefInputSource } from '@shared/types/tool-design';
import { Button } from '@/components/ui';
import { EditableText } from './usage-sheet/EditableText';
import { SheetRow, SheetSection } from './usage-sheet/SheetLines';
import { removeAt, replaceAt } from './usage-sheet/sheet-edits';
import { CheckProofLines } from './CheckProofLines';
import { AppDataSection } from './usage-sheet/AppDataSection';
import { MethodSection } from './usage-sheet/MethodSection';
import { LinksSections } from './usage-sheet/LinksSection';
import { AddLine, splitLabelValue } from './usage-sheet/AddLine';
import { UsesSection } from './usage-sheet/UsesSection';
import { SuccessLine } from './usage-sheet/SuccessLine';
import { methodShortfall } from '@shared/forge-usage-sheet';

const INPUT_SOURCE_LABELS: Record<DesignBriefInputSource, string> = {
  user: 'à chaque lancement', profile: 'Profil', crm: 'CRM', default: 'règle fixe',
};

const SOURCE_KIND_LABELS: Record<UsageSheetSourceKind, string> = {
  connected: 'connectée', to_connect: 'à connecter', web: 'web', document: 'document', profile: 'Profil', crm: 'CRM',
};

const PROMISE_GROUPS: Array<{ mode: UsageSheetPromiseMode; title: string }> = [
  { mode: 'domino', title: 'Domino le fait' },
  { mode: 'with_you', title: 'Domino le fait avec vous' },
  { mode: 'not_yet', title: 'Pas encore possible' },
];

const TRIGGER_LABELS: Record<ForgeUsageSheet['usage']['trigger'], string> = {
  on_demand: 'À la demande', schedule: 'À une cadence', event: 'Sur un événement',
};

/**
 * The usage sheet of the Forge FDE (decision 01/10/2026, F1): the tool as a
 * product, short enough to read in 30 seconds. The user edits or deletes any
 * line in place — saved at once, no AI call — and Domino takes the sheet as it
 * stands at its next answer. Nothing is built before this exact sheet is
 * validated. Justifications stay one click away.
 */
export const UsageSheetCard = ({ sheet, validated, toolBuilt, building, writing = false, alwaysOpen = false, onValidate, onChange }: {
  sheet: ForgeUsageSheet;
  /** This exact sheet was validated and the tool built from it. */
  validated: boolean;
  /** A tool exists from an earlier version of the sheet: validating rebuilds it. */
  toolBuilt: boolean;
  /** A turn is running: no validation nor edit until it ends. */
  building: boolean;
  /** Domino is writing this sheet right now: lines appear as they are written. */
  writing?: boolean;
  /** Shown as the Forge's « Fiche » step: always in full, never folded. */
  alwaysOpen?: boolean;
  onValidate: () => void;
  onChange: (sheet: ForgeUsageSheet) => void;
}) => {
  const [open, setOpen] = useState(!validated);
  const [showWhy, setShowWhy] = useState(false);
  const editable = !building && !writing;
  const decisions = sheet.doubts.filter((doubt) => doubt.needsDecision).length;
  // A method short of its success line is built only knowingly (22/10).
  const anyway = methodShortfall(sheet) !== null;
  const set = (patch: Partial<ForgeUsageSheet>) => onChange({ ...sheet, ...patch });
  const text = (value: string, label: string, onSave: (value: string) => void, className?: string) =>
    <EditableText value={value} label={label} editable={editable} onSave={onSave} className={className} />;

  if (validated && !open && !writing && !alwaysOpen) {
    return <div className="flex flex-wrap items-center gap-3 rounded-xl border border-domino-cardBorder bg-domino-white px-4 py-3 text-sm">
      <CircleCheck className="h-4 w-4 text-emerald-700" aria-hidden="true" />
      <span className="text-domino-ink">Fiche validée : l’outil est construit à partir de cette fiche</span>
      <button type="button" className="text-domino-aubergine underline underline-offset-4" onClick={() => setOpen(true)}>
        Voir la fiche
      </button>
    </div>;
  }

  return <article aria-label="Fiche d’usage" aria-busy={writing} className="space-y-4 rounded-2xl border border-domino-cardBorder bg-domino-white p-5">
    <header className="space-y-2">
      <div className="flex items-center justify-between gap-3">
        <p className="text-xs font-medium uppercase tracking-wide text-domino-inkSoft">Fiche d’usage</p>
        <div className="flex items-center gap-3">
          <button type="button" className="text-xs text-domino-aubergine underline underline-offset-4" onClick={() => setShowWhy(!showWhy)}>
            {showWhy ? 'Masquer les justifications' : 'Afficher les justifications'}
          </button>
          {/* The action is where the eye starts, not only at the end of a long sheet. */}
          {!validated && !writing && (
            <Button size="sm" onClick={onValidate} disabled={building}>
              {anyway ? (toolBuilt ? 'Reconstruire quand même' : 'Construire quand même') : toolBuilt ? 'Valider et reconstruire' : 'Valider et construire'}
            </Button>
          )}
        </div>
      </div>
      {sheet.title && <h2 className="text-lg font-semibold text-domino-ink">
        {text(sheet.title, 'Nom de l’outil', (title) => set({ title }))}
      </h2>}
      {sheet.cap.problem && <div className="rounded-xl bg-domino-muted/60 px-4 py-3 text-sm">
        <p className="text-domino-ink"><span className="font-medium">Le cap : </span>
          {text(sheet.cap.problem, 'Le cap', (problem) => set({ cap: { ...sheet.cap, problem } }))}</p>
        {sheet.cap.successCriterion && <p className="mt-1 text-domino-inkSoft"><span className="font-medium">Réussi si : </span>
          {text(sheet.cap.successCriterion, 'Critère de réussite', (successCriterion) => set({ cap: { ...sheet.cap, successCriterion } }))}</p>}
        {sheet.success && <SuccessLine success={sheet.success} text={text} onChange={(success) => set({ success })} />}
      </div>}
    </header>

    {sheet.job.who && <SheetSection title="Pour qui, quand">
      <p className="text-sm text-domino-ink">
        {text(sheet.job.who, 'Pour qui', (who) => set({ job: { ...sheet.job, who } }))}
        {sheet.job.moment && <> · {text(sheet.job.moment, 'À quel moment', (moment) => set({ job: { ...sheet.job, moment } }))}</>}
      </p>
      {sheet.usage.rhythm && <p className="text-sm text-domino-inkSoft">
        {TRIGGER_LABELS[sheet.usage.trigger]} ({text(sheet.usage.rhythm, 'Rythme', (rhythm) => set({ usage: { ...sheet.usage, rhythm } }))}) ·{' '}
        <button type="button" disabled={!editable} className="underline decoration-dotted underline-offset-4 disabled:no-underline"
          onClick={() => set({ usage: { ...sheet.usage, audience: sheet.usage.audience === 'team' ? 'me' : 'team' } })}>
          {sheet.usage.audience === 'team' ? 'pour l’équipe' : 'pour vous'}
        </button>
      </p>}
      {showWhy && sheet.job.nextDecision && <p className="text-sm text-domino-inkSoft">Ensuite : {sheet.job.nextDecision}</p>}
    </SheetSection>}

    {sheet.inputs.length > 0 && <SheetSection title="Entrées">
      {sheet.inputs.map((input, index) => <SheetRow key={`${index}:${input.label}`} showWhy={showWhy} why={input.why}
        removeLabel={`Supprimer l’entrée ${input.label}`} onRemove={editable ? () => set({ inputs: removeAt(sheet.inputs, index) }) : undefined}>
        <span className="font-medium">{text(input.label, `Entrée ${input.label}`, (label) => set({ inputs: replaceAt(sheet.inputs, index, { label }) }))}</span>
        {' : '}{text(input.proposal, `Valeur de ${input.label}`, (proposal) => set({ inputs: replaceAt(sheet.inputs, index, { proposal }) }))}
        <span className="text-domino-inkSoft"> · {INPUT_SOURCE_LABELS[input.source]}</span>
        {input.testValue && <span className="text-domino-inkSoft"> · essai : {text(input.testValue, `Valeur d’essai de ${input.label}`,
          (testValue) => set({ inputs: replaceAt(sheet.inputs, index, { testValue }) }))}</span>}
      </SheetRow>)}
      {editable && <AddLine label="Ajouter une entrée" placeholder="Ville : Lyon, Paris"
        onAdd={(line) => {
          const { label, value } = splitLabelValue(line);
          set({ inputs: [...sheet.inputs, { label, proposal: value, source: 'user', why: '', status: 'confirmed' }] });
        }} />}
    </SheetSection>}

    {(sheet.result.form || sheet.result.outputs.length > 0) && <SheetSection title={sheet.result.form ? `Le résultat : ${sheet.result.form}` : 'Le résultat'}>
      {sheet.result.outputs.map((output, index) => <SheetRow key={`${index}:${output.label}`} showWhy={showWhy} why={output.why}
        removeLabel={`Supprimer ${output.label}`}
        onRemove={editable && sheet.result.outputs.length > 1
          ? () => set({ result: { ...sheet.result, outputs: removeAt(sheet.result.outputs, index) } })
          : undefined}>
        {text(output.label, `Résultat ${output.label}`, (label) => set({ result: { ...sheet.result, outputs: replaceAt(sheet.result.outputs, index, { label }) } }))}
      </SheetRow>)}
      {editable && <AddLine label="Ajouter au résultat" placeholder="Ville de l’entreprise"
        onAdd={(label) => set({ result: { ...sheet.result, outputs: [...sheet.result.outputs, { label, why: '', status: 'confirmed' }] } })} />}
      {sheet.result.afterwards && <p className="text-sm text-domino-inkSoft">Ensuite : {text(sheet.result.afterwards, 'Ensuite',
        (afterwards) => set({ result: { ...sheet.result, afterwards } }))}</p>}
    </SheetSection>}

    {sheet.appData && <AppDataSection data={sheet.appData} editable={editable} onChange={(appData) => set({ appData })} />}

    <LinksSections sheet={sheet} editable={editable} showWhy={showWhy} text={text} set={set} />

    {sheet.sources.length > 0 && <SheetSection title="Sources">
      {sheet.sources.map((source, index) => <SheetRow key={`${index}:${source.label}`} showWhy={showWhy} why={source.why}
        removeLabel={`Supprimer la source ${source.label}`} onRemove={editable ? () => set({ sources: removeAt(sheet.sources, index) }) : undefined}>
        {text(source.label, `Source ${source.label}`, (label) => set({ sources: replaceAt(sheet.sources, index, { label }) }))}
        <span className="text-domino-inkSoft"> · {SOURCE_KIND_LABELS[source.kind]}</span>
      </SheetRow>)}
    </SheetSection>}

    {(sheet.uses?.length ?? 0) > 0 && <UsesSection uses={sheet.uses ?? []} editable={editable} showWhy={showWhy} text={text}
      onChange={(uses) => set({ uses })} />}

    {sheet.method && <MethodSection sheet={sheet} method={sheet.method} showWhy={showWhy} text={text}
      onChange={(method) => set({ method })} />}

    {sheet.promises.length > 0 && <SheetSection title="Ce que Domino promet">
      {PROMISE_GROUPS.map(({ mode, title }) => {
        const indexes = sheet.promises.flatMap((promise, index) => (promise.mode === mode ? [index] : []));
        if (indexes.length === 0) return null;
        return <div key={mode} className="space-y-1">
          <p className="text-sm text-domino-inkSoft">{title}</p>
          {indexes.map((index) => {
            const promise = sheet.promises[index]!;
            return <SheetRow key={`${index}:${promise.promise}`} showWhy={showWhy || mode !== 'domino'} why={promise.detail}
              removeLabel={`Supprimer la promesse ${promise.promise}`} onRemove={editable ? () => set({ promises: removeAt(sheet.promises, index) }) : undefined}>
              {text(promise.promise, 'Promesse', (value) => set({ promises: replaceAt(sheet.promises, index, { promise: value }) }))}
            </SheetRow>;
          })}
        </div>;
      })}
    </SheetSection>}

    {sheet.ideas.length > 0 && <SheetSection title="Idées en plus">
      {sheet.ideas.map((idea, index) => <SheetRow key={`${index}:${idea.idea}`} showWhy={showWhy} why={idea.why}
        removeLabel={`Écarter l’idée ${idea.idea}`} onRemove={editable ? () => set({ ideas: removeAt(sheet.ideas, index) }) : undefined}>
        {text(idea.idea, 'Idée', (value) => set({ ideas: replaceAt(sheet.ideas, index, { idea: value }) }))}
      </SheetRow>)}
      {editable && <AddLine label="Ajouter une idée" placeholder="Une idée en plus"
        onAdd={(idea) => set({ ideas: [...sheet.ideas, { idea, why: '' }] })} />}
    </SheetSection>}

    {sheet.doubts.length > 0 && <SheetSection title="Points à trancher">
      {sheet.doubts.map((doubt, index) => <SheetRow key={`${index}:${doubt.doubt}`} showWhy why={<>
        Proposition : {text(doubt.proposal, 'Proposition', (proposal) => set({ doubts: replaceAt(sheet.doubts, index, { proposal }) }))}
        {showWhy && doubt.consequence && <span className="block">Conséquence : {doubt.consequence}</span>}
      </>} removeLabel={`Retirer le point ${doubt.doubt}`} onRemove={editable ? () => set({ doubts: removeAt(sheet.doubts, index) }) : undefined}>
        <span className="flex items-start gap-2 font-medium">
          {doubt.needsDecision && <CircleAlert className="mt-0.5 h-4 w-4 shrink-0 text-[var(--domino-warning)]" aria-label="À trancher" />}
          {doubt.doubt}
        </span>
      </SheetRow>)}
    </SheetSection>}

    {(sheet.checks?.length ?? 0) > 0 && <SheetSection title="Vérifié en direct">
      <CheckProofLines checks={sheet.checks ?? []} />
    </SheetSection>}

    {writing ? <footer className="border-t border-domino-cardBorder pt-4 text-sm text-domino-inkSoft">Domino rédige la fiche…</footer>
      : <footer className="flex flex-wrap items-center gap-3 border-t border-domino-cardBorder pt-4">
        {validated
          ? !alwaysOpen && <Button variant="secondary" onClick={() => setOpen(false)}>Replier la fiche</Button>
          : <Button onClick={onValidate} disabled={building}>
            {anyway
              ? (toolBuilt ? 'Reconstruire l’outil quand même' : 'Construire l’outil quand même')
              : toolBuilt ? 'Valider la fiche et reconstruire l’outil' : 'Valider la fiche et construire l’outil'}
          </Button>}
        <p className="text-xs text-domino-inkSoft">
          {decisions > 0 ? `${decisions} point${decisions > 1 ? 's' : ''} à trancher. ` : ''}
          Cliquez sur une ligne pour la modifier, sur × pour la retirer, sur + pour en ajouter une.
        </p>
      </footer>}
  </article>;
};
